/**
 * Chart-komponentin renderöintiavut (AB-WP6.1) — puhtaita funktioita
 * (testattavissa ilman DOM-bootia): rivien muunto datapisteiksi ja itsenäisen
 * inline-SVG:n piirto (bar/line/area/pie). Verkkokutsu itse tehdään
 * runtime-sillan ({@link ./bridge}) kautta runtime.ts:ssä; nämä vain muuntavat
 * vastauksen SVG:ksi.
 *
 * Ei ulkoisia kirjastoja: serving-CSP estää CDN-skriptit, joten kaavio
 * piirretään käsin SVG-elementteinä. Kaikki teksti kirjoitetaan
 * {@code textContent}illa (ei innerHTML) — käyttäjän dataa sisältävät labelit
 * eivät voi injektoida markkausta (WP5.1-escapointi-checklist).
 */

import { extractRows, resolvePath } from './dataSources';

export type ChartType = 'bar' | 'line' | 'area' | 'pie';

export interface ChartPoint {
  label: string;
  value: number;
}

/**
 * Yksi sarja (WP-KL7, CRM-harjoituksen löydös `E-4`). Kaavio otti ennen yhden
 * `labelPath`in ja yhden `valuePath`in, joten "toteuma vs. tavoite" tai "sähkö vs.
 * kaukolämpö" ei ollut ilmaistavissa lainkaan — kierto oli kaksi kaaviota
 * vierekkäin, mikä ei vastaa samaan kysymykseen: vertailu oli juuri se mitä
 * kysyttiin.
 */
export interface ChartSeries {
  /** Selitteen teksti. Yhden sarjan kaaviossa tyhjä = ei selitettä. */
  label: string;
  points: ChartPoint[];
}

export interface ChartOptions {
  type: ChartType;
  /** Piirtoalueen korkeus loogisina yksikköinä (= px, SPA skaalaa leveyden). */
  height: number;
  /** Kategoriavärit; kierrätetään pisteiden yli (bar/pie). */
  palette: string[];
  /** Näytä arvolabelit pylväiden/pisteiden kohdalla. */
  showValues: boolean;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Kiinteä looginen leveys; SVG skaalautuu säiliön leveyteen viewBoxilla. */
const VIEW_WIDTH = 600;

const PALETTES: Record<string, string[]> = {
  default: ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'],
  cool: ['#2563eb', '#0891b2', '#0d9488', '#7c3aed', '#4f46e5', '#0284c7'],
  warm: ['#dc2626', '#ea580c', '#d97706', '#ca8a04', '#db2777', '#e11d48'],
};

/** Palauta nimetty paletti, tuntematon/tyhjä → default. */
export function resolvePalette(name: string | null | undefined): string[] {
  return (name && PALETTES[name]) || PALETTES.default;
}

/**
 * Muunna vastausrivit datapisteiksi label-/value-poluilla. Pisteet joiden
 * arvo ei ole äärellinen luku (tai puuttuu) ohitetaan; label pakotetaan
 * merkkijonoksi (puuttuva → 1-pohjainen indeksi).
 */
export function toChartPoints(
  rows: Record<string, unknown>[],
  labelPath: string,
  valuePath: string,
): ChartPoint[] {
  const points: ChartPoint[] = [];
  rows.forEach((row, i) => {
    const rawValue = resolvePath(row, valuePath);
    if (rawValue == null) return;
    const value = Number(rawValue);
    if (!Number.isFinite(value)) return;
    const rawLabel = resolvePath(row, labelPath);
    const label = rawLabel == null ? String(i + 1) : String(rawLabel);
    points.push({ label, value });
  });
  return points;
}

/**
 * Muunna vastausrivit sarjoiksi. Yksi polku → yksi sarja, jolloin lopputulos on
 * sama kuin {@link toChartPoints}illa. Sarjan otsikko on `label` jos annettu,
 * muuten polku itse — nimetön sarja selitteessä ei kertoisi mitään.
 */
export function toChartSeries(
  rows: Record<string, unknown>[],
  labelPath: string,
  valuePaths: { path: string; label?: string }[],
): ChartSeries[] {
  return valuePaths
    .filter((v) => v && typeof v.path === 'string' && v.path !== '')
    .map((v) => ({
      label: v.label && v.label !== '' ? v.label : v.path,
      points: toChartPoints(rows, labelPath, v.path),
    }))
    .filter((s) => s.points.length > 0);
}

function svgEl(doc: Document, tag: string, attrs: Record<string, string | number>): SVGElement {
  const el = doc.createElementNS(SVG_NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/** SVG-teksti; sisältö kirjoitetaan textContentilla → ei markup-injektiota. */
function svgText(
  doc: Document,
  x: number,
  y: number,
  text: string,
  cls: string,
  anchor: string,
): SVGElement {
  const el = svgEl(doc, 'text', { x, y, 'text-anchor': anchor, class: cls });
  el.textContent = text;
  return el;
}

/**
 * Piirrä kaavio {@code host}iin annettujen pisteiden pohjalta. Tyhjentää
 * hostin ensin; tyhjä pisteistö jättää hostin tyhjäksi (status-viesti hoitaa
 * "no data"). Palauttaa piirretyn SVG-elementin (tai null jos ei pisteitä).
 */
export function drawChart(
  host: HTMLElement,
  data: ChartPoint[] | ChartSeries[],
  opts: ChartOptions,
  doc: Document = host.ownerDocument,
): SVGSVGElement | null {
  host.textContent = '';
  // Kutsuja voi antaa joko pisteitä (yksi sarja, entinen kutsutapa) tai sarjoja.
  const series: ChartSeries[] = Array.isArray(data) && data.length > 0 && 'points' in data[0]
    ? (data as ChartSeries[])
    : [{ label: '', points: (data as ChartPoint[]) ?? [] }];
  const points = series[0]?.points ?? [];
  if (series.every((s) => s.points.length === 0)) return null;

  const height = Math.max(80, Math.min(opts.height || 240, 800));
  // preserveAspectRatio="xMidYMid meet": säilyttää mittasuhteet (teksti ei
  // veny) ja skaalaa SVG:n säiliön leveyteen viewBoxin kautta.
  const svg = svgEl(doc, 'svg', {
    viewBox: `0 0 ${VIEW_WIDTH} ${height}`,
    preserveAspectRatio: 'xMidYMid meet',
    role: 'img',
  }) as SVGSVGElement;

  if (opts.type === 'pie') {
    // Piirakalle monisarjaisuus ei tarkoita mitään; validointi hylkää sen jo
    // tallennuksessa, ja tässä piirretään ensimmäinen sarja.
    drawPie(doc, svg, points, opts, height);
  } else {
    drawCartesian(doc, svg, series, opts, height);
  }
  host.appendChild(svg);
  if (series.length > 1) appendLegend(host, series, opts, doc);
  return svg;
}

/**
 * Selite monisarjaiselle kaaviolle. HTML eikä SVG, jotta se rivittyy kapealla
 * näytöllä; värit tulevat samasta paletista samassa järjestyksessä kuin sarjat.
 */
function appendLegend(
  host: HTMLElement, series: ChartSeries[], opts: ChartOptions, doc: Document,
): void {
  const legend = doc.createElement('div');
  legend.className = 'sp-chart-legend';
  series.forEach((s, i) => {
    const item = doc.createElement('span');
    item.className = 'sp-chart-legend-item';
    const swatch = doc.createElement('span');
    swatch.className = 'sp-chart-legend-swatch';
    swatch.style.background = opts.palette[i % opts.palette.length];
    const text = doc.createElement('span');
    text.textContent = s.label;
    item.appendChild(swatch);
    item.appendChild(text);
    legend.appendChild(item);
  });
  host.appendChild(legend);
}

/**
 * Bar/line/area: yhteinen akselisto + tyyppikohtaiset sarjat.
 *
 * <p>Yhden sarjan tulos on tarkoituksella sama kuin ennen monisarjaisuutta
 * (WP-KL7): pylväät saavat KATEGORIAvärit ja viiva paletin ensimmäisen värin.
 * Monisarjaisessa sama väri tarkoittaa samaa sarjaa — muuten selite ei voisi
 * kertoa mitään. Vaihdos on siis semanttinen eikä kosmeettinen, ja siksi se
 * tapahtuu vain kun sarjoja on enemmän kuin yksi.
 */
function drawCartesian(
  doc: Document,
  svg: SVGSVGElement,
  series: ChartSeries[],
  opts: ChartOptions,
  height: number,
): void {
  const left = 8;
  const right = 8;
  const top = opts.showValues ? 20 : 10;
  const bottom = 26;
  const plotW = VIEW_WIDTH - left - right;
  const plotH = height - top - bottom;
  const baseline = top + plotH;
  const multi = series.length > 1;
  // Kategoriat luetaan pisimmästä sarjasta: eri pituiset sarjat eivät saa
  // pudottaa akselia lyhyimmän mukaan.
  const spine = series.reduce((a, b) => (b.points.length > a.points.length ? b : a), series[0]);
  const n = spine.points.length;
  // Yhteinen maksimi kaikkien sarjojen yli — sarjakohtainen skaala tekisi
  // vertailusta valheellisen, mikä on juuri se mitä monisarjaisuudella haetaan.
  const max = Math.max(
    ...series.flatMap((s) => s.points.map((p) => (p.value > 0 ? p.value : 0))), 1,
  );

  // Nollaviiva. Väri tulee CSS:stä (.sp-chart-grid), EI inline-attribuutista:
  // SVG-presentaatioattribuutti ei resolvoi var()-viittausta, joten kovakoodattu
  // stroke jäisi vaaleaksi myös tummassa teemassa (app-builder-theme-plan.md §2.6).
  svg.appendChild(svgEl(doc, 'line', {
    class: 'sp-chart-grid',
    x1: left, y1: baseline, x2: left + plotW, y2: baseline,
  }));

  if (opts.type === 'bar') {
    const slot = plotW / n;
    const groupW = slot * 0.68;
    const barW = groupW / series.length;
    spine.points.forEach((_, i) => {
      series.forEach((s, si) => {
        const p = s.points[i];
        if (!p) return;
        const h = p.value > 0 ? (p.value / max) * plotH : 0;
        const x = left + i * slot + (slot - groupW) / 2 + si * barW;
        const y = baseline - h;
        svg.appendChild(svgEl(doc, 'rect', {
          class: 'sp-chart-bar', x, y, width: barW, height: h,
          // Yksi sarja → kategoriaväri (kuten ennen); monta → sarjaväri.
          fill: opts.palette[(multi ? si : i) % opts.palette.length], rx: 2,
        }));
        if (opts.showValues) {
          svg.appendChild(svgText(doc, x + barW / 2, y - 4, formatValue(p.value), 'sp-chart-value', 'middle'));
        }
      });
      svg.appendChild(svgText(
        doc, left + i * slot + slot / 2, height - 8, spine.points[i].label, 'sp-chart-label', 'middle',
      ));
    });
    return;
  }

  // line / area: pisteet tasavälein plotW:n yli, yksi polyline per sarja.
  const xOf = (i: number) => (n === 1 ? left + plotW / 2 : left + (i / (n - 1)) * plotW);
  const yOf = (v: number) => baseline - (v > 0 ? (v / max) * plotH : 0);

  series.forEach((s, si) => {
    const color = opts.palette[si % opts.palette.length];
    const coords = s.points.map((p, i) => `${xOf(i)},${yOf(p.value)}`).join(' ');
    if (opts.type === 'area') {
      const areaPts = `${xOf(0)},${baseline} ${coords} ${xOf(s.points.length - 1)},${baseline}`;
      svg.appendChild(svgEl(doc, 'polygon', { class: 'sp-chart-area', points: areaPts, fill: color }));
    }
    svg.appendChild(svgEl(doc, 'polyline', { class: 'sp-chart-line', points: coords, stroke: color }));
    s.points.forEach((p, i) => {
      svg.appendChild(svgEl(doc, 'circle', {
        class: 'sp-chart-dot', cx: xOf(i), cy: yOf(p.value), r: 3, fill: color,
      }));
      if (opts.showValues) {
        svg.appendChild(svgText(doc, xOf(i), yOf(p.value) - 8, formatValue(p.value), 'sp-chart-value', 'middle'));
      }
    });
  });
  // Kategorialabelit kerran, ei kerran per sarja.
  spine.points.forEach((p, i) => {
    svg.appendChild(svgText(doc, xOf(i), height - 8, p.label, 'sp-chart-label', 'middle'));
  });
}

/** Piirakka: positiiviset arvot sektoreina + selite oikealle. */
function drawPie(
  doc: Document,
  svg: SVGSVGElement,
  points: ChartPoint[],
  opts: ChartOptions,
  height: number,
): void {
  const positive = points.filter((p) => p.value > 0);
  const total = positive.reduce((s, p) => s + p.value, 0);
  const cy = height / 2;
  const cx = Math.min(cy, 160);
  const r = Math.max(20, cx - 12);

  if (total <= 0) {
    // Ei positiivisia arvoja — pieni "tyhjä" -kehä, ei sektoreita.
    svg.appendChild(svgEl(doc, 'circle', { class: 'sp-chart-grid', cx, cy, r }));
    return;
  }

  let angle = -Math.PI / 2; // aloita ylhäältä
  positive.forEach((p, i) => {
    const slice = (p.value / total) * Math.PI * 2;
    const a0 = angle;
    const a1 = angle + slice;
    angle = a1;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const largeArc = slice > Math.PI ? 1 : 0;
    // Yksi sektori = koko ympyrä: piirrä kehänä (arc-path degeneroituisi).
    const d = positive.length === 1
      ? `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.01} ${cy - r} Z`
      : `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1} Z`;
    svg.appendChild(svgEl(doc, 'path', {
      class: 'sp-chart-slice', d, fill: opts.palette[i % opts.palette.length],
    }));
  });

  // Selite: väriruutu + label per sektori.
  const legendX = cx + r + 24;
  const rowH = 18;
  const startY = Math.max(14, cy - (positive.length * rowH) / 2);
  positive.forEach((p, i) => {
    const y = startY + i * rowH;
    svg.appendChild(svgEl(doc, 'rect', {
      x: legendX, y: y - 9, width: 11, height: 11, rx: 2,
      fill: opts.palette[i % opts.palette.length],
    }));
    const pct = Math.round((p.value / total) * 100);
    const legend = svgText(doc, legendX + 16, y, '', 'sp-chart-label', 'start');
    // label + prosentti erikseen textContentina (ei markup-injektiota).
    legend.textContent = `${p.label} — ${pct}%`;
    svg.appendChild(legend);
  });
}

/** Numeron tiivis esitys datalabeliin (kokonaisluku ilman desimaaleja). */
function formatValue(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
}

/**
 * Poimi chartin inline-data (staticData-script) rivitaulukoksi. Sama
 * poimintasemantiikka kuin API-vastauksella ({@link extractRows}).
 */
export function chartStaticRows(json: string | null | undefined): Record<string, unknown>[] {
  return extractRows(json || '[]');
}
